import { useMemo, createContext, useContext, useState, useEffect } from "react"
import { useStaticQuery, graphql } from "gatsby"
import TagManager, { DataLayerArgs, TagManagerArgs } from "react-gtm-module"
import { useCore } from "@app/hooks/useCore"
import { isTrackingAllowed } from "@app/utils/tracking"
import { useConfigContext } from "./config"

export type ContextProps = {
  tracked: boolean
  setTracked: React.Dispatch<React.SetStateAction<boolean>>
  latestVersion: boolean
  colourOptionName: string
  gtm: {
    dataLayer: (dataLayerArgs: DataLayerArgs) => void
    initialize: (tagManagerArgs: TagManagerArgs) => void
  }
}

export const TrackingContext = createContext<ContextProps | undefined>(undefined)

export const TrackingProvider: React.FC = ({ children }) => {
  const {
    settings: {
      product: { colourOptionName },
    },
  } = useConfigContext()
  const {
    helpers: { isDomReady },
  } = useCore()
  const [tracked, setTracked] = useState(false)

  const { tracking } = useStaticQuery<GatsbyTypes.StaticTrackingQuery>(graphql`
    query StaticTracking {
      tracking: sanitySettingTracking {
        facebookAppId
        googleSiteVerify
        googleTagManagerId
      }
    }
  `)

  const contextValue = useMemo<ContextProps>(
    () => ({
      tracked,
      setTracked,
      latestVersion: true, //"GA4"
      colourOptionName, //"Colour"
      gtm: TagManager,
    }),
    [tracked, setTracked, colourOptionName]
  )

  useEffect(() => {
    if (tracking?.googleTagManagerId && isTrackingAllowed()) {
      TagManager.initialize({
        gtmId: tracking?.googleTagManagerId,
      })
      setTracked(true)
    }
  }, [tracking])

  return isDomReady ? <TrackingContext.Provider value={contextValue}>{children}</TrackingContext.Provider> : <>{children}</>
}

export const useTrackingContext = (): ContextProps => ({ ...useContext(TrackingContext) } as ContextProps)
